import { t } from "@lingui/macro";
import { UserTier } from "@stylingcv/config";
import { useEffect, useState } from "react";

import { useAuthStore } from "../stores/auth";
import {
  canCreateMoreResumes,
  getMaxResumes,
  getUserTierFromSubscription,
  RESUME_LIMIT_CONFIG,
} from "../utils/resume-limits";

/**
 * Hook to manage resume limits based on user subscription and current resume count
 */
export const useResumeLimits = (currentResumeCount = 0) => {
  const { user, subscriptionTier } = useAuthStore();
  const [userTier, setUserTier] = useState<UserTier>(UserTier.GUEST);

  useEffect(() => {
    if (!user) {
      setUserTier(UserTier.GUEST);
    } else if (subscriptionTier) {
      setUserTier(getUserTierFromSubscription(subscriptionTier));
    } else {
      setUserTier(UserTier.FREE);
    }
  }, [user, subscriptionTier]);

  const maxAllowed = getMaxResumes(userTier);
  const resumeCheck = canCreateMoreResumes(userTier, currentResumeCount);

  return {
    // Core limits
    maxAllowed: resumeCheck.maxAllowed,
    remaining: resumeCheck.remaining,
    canCreate: resumeCheck.canCreate,

    // User info
    userTier,
    isGuest: !user,
    isSubscribed: subscriptionTier && subscriptionTier !== "FREE",

    // Helper functions
    getUpgradeMessage: () => {
      if (!user) {
        return t`Sign up to create more resumes`;
      }

      switch (userTier) {
        case UserTier.FREE: {
          return t`Upgrade to Basic to create up to 5 resumes`;
        }
        case UserTier.PREMIUM_BASIC: {
          return t`Upgrade to Professional to create up to 10 resumes`;
        }
        case UserTier.PAID_TOKENS: {
          return t`You can create up to 10 resumes`;
        }
        case UserTier.PREMIUM_ENTERPRISE: {
          return t`You have unlimited resumes`;
        }
        default: {
          return t`Upgrade your plan to create more resumes`;
        }
      }
    },

    getLimitDescription: () => {
      if (!user) {
        return RESUME_LIMIT_CONFIG.GUEST.description;
      }

      switch (userTier) {
        case UserTier.FREE: {
          return RESUME_LIMIT_CONFIG.FREE.description;
        }
        case UserTier.PREMIUM_BASIC: {
          return RESUME_LIMIT_CONFIG.BASIC.description;
        }
        case UserTier.PAID_TOKENS:
        case UserTier.PREMIUM_PRO: {
          return RESUME_LIMIT_CONFIG.PROFESSIONAL.description;
        }
        case UserTier.PREMIUM_ENTERPRISE: {
          return RESUME_LIMIT_CONFIG.ENTERPRISE.description;
        }
        default: {
          return RESUME_LIMIT_CONFIG.FREE.description;
        }
      }
    },

    getNextTierInfo: () => {
      if (!user) {
        return {
          tier: t`Free Account`,
          limit: RESUME_LIMIT_CONFIG.FREE.limit,
          action: t`Sign up`,
        };
      }

      switch (userTier) {
        case UserTier.FREE: {
          return {
            tier: t`Basic Plan`,
            limit: RESUME_LIMIT_CONFIG.BASIC.limit,
            action: t`Upgrade to Basic`,
          };
        }
        case UserTier.PREMIUM_BASIC: {
          return {
            tier: t`Professional Plan`,
            limit: RESUME_LIMIT_CONFIG.PROFESSIONAL.limit,
            action: t`Upgrade to Professional`,
          };
        }
        default: {
          return null;
        }
      }
    },
  };
};

/**
 * Hook specifically for checking if user can create a new resume
 */
export const useCanCreateResume = (currentResumeCount = 0) => {
  const limits = useResumeLimits(currentResumeCount);

  return {
    canCreate: limits.canCreate,
    reason: limits.canCreate
      ? null
      : limits.isGuest
        ? "guest_limit"
        : limits.remaining === 0
          ? "subscription_limit"
          : "unknown",
    upgradeMessage: limits.getUpgradeMessage(),
    nextTier: limits.getNextTierInfo(),
  };
};
